import { model, Model, prop, modelAction } from "mobx-keystone"
import { computed } from "mobx"
import { matchesApiPlatform } from "../../docs/catalog"
import { apiEntriesChunk, searchChunk } from "../../docs/lazy"
import type { ApiEntry, ApiPlatform } from "../../docs/types"

@model("or/DocsBrowser")
export class DocsBrowser extends Model({
  query: prop<string>(""),
  platform: prop<"all" | ApiPlatform>("all"),
  selectedAnchor: prop<string | null>(null)
}) {
  // The API reference is its own chunk (docs/lazy), and the search index
  // another: the page reads the first, a query the second.
  @computed
  get entries(): ApiEntry[] {
    return apiEntriesChunk.current ?? []
  }

  @computed
  get loading() {
    return apiEntriesChunk.current == null
  }

  // Until the search chunk arrives a query filters by platform only.
  @computed
  get matchingIds() {
    const search = this.query.trim() ? searchChunk.current : null
    if (search) return search.matchingApiEntryIds(this.query, this.platform)
    return new Set(
      this.entries.filter((entry) => matchesApiPlatform(entry, this.platform)).map((entry) => entry.id)
    )
  }

  @computed
  get visibleEntries() {
    if (!this.query.trim()) {
      return this.entries.filter(
        (entry) => !entry.nested && this.matchingIds.has(entry.id)
      )
    }
    return this.entries.filter((entry) => this.matchingIds.has(entry.id))
  }

  @computed
  get sections() {
    const groups: { section: string; platform: ApiPlatform; entries: ApiEntry[] }[] = []
    const indexByKey = new Map<string, number>()

    for (const entry of this.visibleEntries) {
      const key = `${entry.platform}:${entry.section}`
      const existing = indexByKey.get(key)
      if (existing == null) {
        indexByKey.set(key, groups.length)
        groups.push({ section: entry.section, platform: entry.platform, entries: [entry] })
      } else {
        groups[existing].entries.push(entry)
      }
    }

    return groups
  }

  @computed
  get toc() {
    return this.sections.map((group) => ({
      section: group.section,
      platform: group.platform,
      items: group.entries.map((entry) => ({
        id: entry.id,
        title: entry.title,
        anchor: entry.anchor,
        kind: entry.kind
      }))
    }))
  }

  @computed
  get resultCount() {
    return this.matchingIds.size
  }

  @computed
  get isFiltering() {
    return this.query.trim().length > 0 || this.platform !== "all"
  }

  @modelAction
  setQuery = (query: string) => {
    this.query = query
  }

  @modelAction
  setPlatform = (platform: "all" | ApiPlatform) => {
    this.platform = platform
  }

  @modelAction
  selectAnchor = (anchor: string | null) => {
    this.selectedAnchor = anchor
  }
}
